// Shared by the report form (Reports page "+ New type") and the company
// settings Form Templates pages (Phelipe 2026-09-09: "one builder
// everywhere"). Two mixins:
//   FormFieldRendererMixin — draws a form-template schema exactly as the
//     crew sees it (text, grids, signatures…). The real report form uses it
//     to render answers; the editor uses it as its live preview.
//   TemplateEditorMixin — the "+ New type" pop-up, the AI import (presign →
//     S3 → extract), and the Google-Forms-style editor: the form pane on the
//     left, the details panel on the right, drag to move, then Save.
// Methods are plain class methods so they read the same as before the
// split; applyMixin copies them onto a Stimulus controller's prototype.
// Generated HTML wires actions through `this.identifier`, so the same code
// serves "report-form" and "template-editor" alike.

export const TEMPLATE_FIELD_TYPES = ["text", "textarea", "number", "date", "time", "checkbox", "radio", "select", "multiselect", "checklist", "table", "signature"]
// Types the old settings builder could save that the editor keeps as-is.
export const KNOWN_FIELD_TYPES = [...TEMPLATE_FIELD_TYPES, "photo", "file", "gps", "weather"]
// Grid columns (Larry's SWPPP, 2026-09-07).
export const TP_TABLE_COLUMN_TYPES = ["yes_no", "text", "textarea", "number", "date", "checkbox"]
export const TP_OPTION_TYPES = ["radio", "select", "multiselect", "checklist"]

export function applyMixin(target, source) {
  for (const name of Object.getOwnPropertyNames(source.prototype)) {
    if (name === "constructor") continue
    // A method the controller defines itself wins over the mixin's.
    if (Object.prototype.hasOwnProperty.call(target.prototype, name)) continue
    Object.defineProperty(target.prototype, name, Object.getOwnPropertyDescriptor(source.prototype, name))
  }
}

export class FormFieldRendererMixin {
  // Shared renderer for a form-template schema — used by the real report
  // form above and by the 22.1 template live-preview pane.
  _formTemplateHtml(schema, formData) {
    let html = ""

    ;(schema.sections || []).forEach(section => {
      const fields = (section.fields || []).filter(f => f.type !== "__spacer")
      if (fields.length === 0) return

      html += `<div class="border-t border-gray-200 pt-4" data-section-key="${this._escAttr(section.key || "")}"${section.anchor ? ` data-section-anchor="${this._escAttr(section.anchor)}"` : ""}>`
      // A place on a built-in form (Studio, 2026-10-17) has no heading of its own.
      if (section.label || !section.anchor) html += `<h3 class="text-sm font-semibold text-gray-700 mb-3">${this._escHtml(section.label || section.key)}</h3>`
      html += `<div class="grid grid-cols-1 sm:grid-cols-6 gap-4">`

      fields.forEach(field => {
        // 6-unit grid: third=2, half=3, full=6 (see form_builder_controller).
        // Signature blocks always take the full row — the "sent to …" state
        // needs the room (Phelipe 2026-09-07).
        const colClass = field.type === "signature" ? "sm:col-span-6"
          : ({ third: "sm:col-span-2", half: "sm:col-span-3", full: "sm:col-span-6" }[field.width] || "sm:col-span-6")
        const saved = formData[field.key]
        // Follow-up questions ("If yes, …") stay hidden until the controlling
        // answer matches (Phelipe 2026-09-07) — see _applyShowIf.
        const cond = field.show_if && field.show_if.field
          ? ` data-show-if-field="${this._escAttr(field.show_if.field)}" data-show-if-equals="${this._escAttr(field.show_if.equals || "Yes")}"` : ""
        html += `<div class="${colClass}"${cond}>${this._renderFormField(field, saved)}</div>`
      })

      html += `</div></div>`
    })

    return html
  }

  _renderFormField(field, savedValue) {
    const req = field.required ? '<span class="text-red-500">*</span>' : ""
    const reqAttr = field.required ? 'data-required' : ""
    const help = field.help_text ? `<p class="text-xs text-gray-400 mt-1">${this._escHtml(field.help_text)}</p>` : ""
    const name = `form_data[${field.key}]`
    // Pre-printed text from the paper form (Larry's SWPPP: "Standard
    // Frequency: 14 days and within 24 hours…") shows up filled in, editable.
    let val = savedValue !== undefined && savedValue !== null ? savedValue : (field.default != null ? field.default : "")
    // "today" on a date field (the inspection date) pre-fills the current day
    // in the picker's Y-m-d storage format (Phelipe 2026-09-07).
    if (field.type === "date" && String(val).trim().toLowerCase() === "today") {
      const d = new Date()
      val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
    }
    const inputClass = "w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
    let input = ""

    switch (field.type) {
      case "text":
        input = `<input type="text" name="${name}" value="${this._escAttr(val)}" placeholder="${this._escAttr(field.placeholder || "")}" ${reqAttr} class="${inputClass}">`
        break
      case "textarea":
        input = `<textarea name="${name}" rows="3" placeholder="${this._escAttr(field.placeholder || "")}" ${reqAttr} class="${inputClass} resize-y">${this._escHtml(val)}</textarea>`
        break
      case "number":
        input = `<input type="number" name="${name}" value="${this._escAttr(val)}" placeholder="${this._escAttr(field.placeholder || "0")}" ${reqAttr} class="${inputClass}">`
        break
      case "date":
        input = `<div class="relative">
          <input type="text" name="${name}" value="${this._escAttr(val)}" data-controller="datepicker" placeholder="Select date..." ${reqAttr} class="${inputClass}">
        </div>`
        break
      case "time":
        input = `<input type="time" name="${name}" value="${this._escAttr(val)}" ${reqAttr} class="${inputClass}">`
        break
      case "checkbox":
        input = `<input type="hidden" name="${name}" value="0">
          <label class="inline-flex items-center gap-2">
            <input type="checkbox" name="${name}" value="1" ${val === "1" || val === true ? "checked" : ""} class="rounded border-gray-300 text-brand focus:ring-brand">
            <span class="text-sm text-gray-600">Yes</span>
          </label>`
        break
      case "radio":
        input = (field.options || []).map(o =>
          `<label class="inline-flex items-center gap-2 mr-4">
            <input type="radio" name="${name}" value="${this._escAttr(o)}" ${val === o ? "checked" : ""} class="border-gray-300 text-brand focus:ring-brand">
            <span class="text-sm text-gray-600">${this._escHtml(o)}</span>
          </label>`
        ).join("")
        break
      case "select":
        input = `<select name="${name}" ${reqAttr} class="${inputClass} cursor-pointer">
          <option value="">Select...</option>
          ${(field.options || []).map(o => `<option value="${this._escAttr(o)}" ${val === o ? "selected" : ""}>${this._escHtml(o)}</option>`).join("")}
        </select>`
        break
      case "multiselect":
        input = `<select name="${name}[]" multiple ${reqAttr} class="${inputClass}" style="min-height:80px">
          ${(field.options || []).map(o => {
            const sel = Array.isArray(val) && val.includes(o) ? "selected" : ""
            return `<option value="${this._escAttr(o)}" ${sel}>${this._escHtml(o)}</option>`
          }).join("")}
        </select>`
        break
      case "checklist": {
        const checked = Array.isArray(val) ? val : (val ? [val] : [])
        input = `<div class="space-y-1.5">${(field.options || []).map(o =>
          `<label class="flex items-center gap-2">
            <input type="checkbox" name="${name}[]" value="${this._escAttr(o)}" ${checked.includes(o) ? "checked" : ""} class="rounded border-gray-300 text-brand focus:ring-brand">
            <span class="text-sm text-gray-600">${this._escHtml(o)}</span>
          </label>`
        ).join("")}</div>`
        break
      }
      case "table":
        input = this._renderTableField(field, val, name)
        break
      case "signature":
        input = this._renderSignatureField(field, val, name)
        break
      case "photo":
        input = `<div class="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
          <p class="text-sm text-gray-400">Photo upload area</p>
        </div>`
        break
      default:
        input = `<input type="text" name="${name}" value="${this._escAttr(val)}" ${reqAttr} class="${inputClass}">`
    }

    return `
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">${this._escHtml(field.label)} ${req}</label>
        ${input}
        ${help}
        <span data-error-msg class="hidden text-xs text-red-500 mt-0.5">This field is required</span>
      </div>
    `
  }

  // A grid answer (Larry's SWPPP, 2026-09-07): the schema's fixed rows plus
  // optional blank rows, one input per column on every row, laid out like the
  // paper form. Values post as form_data[key][r1][col] for fixed rows and
  // form_data[key][x1][col] for blank rows (__label carries the typed name).
  _renderTableField(field, saved, name) {
    const data = saved && typeof saved === "object" && !Array.isArray(saved) ? saved : {}
    const stacked = field.layout === "stacked"
    const fixed = field.rows || []
    let rows = fixed.map((label, i) => this._tableRowHtml(field, name, `r${i + 1}`, data[`r${i + 1}`] || {}, label, i + 1, false)).join("")
    // Added rows: saved on this report (p*/x* keys) plus the project's
    // remembered rows not already here. The crew names them; × forgets them.
    const dynamic = Object.keys(data).filter(k => /^[px]\d+$/.test(k))
      .sort((a, b) => (a[0] === b[0] ? parseInt(a.slice(1), 10) - parseInt(b.slice(1), 10) : (a[0] === "p" ? -1 : 1)))
      .map(k => ({ key: k, vals: data[k] || {}, label: (data[k] || {}).__label || "", project: k[0] === "p" }))
    const seen = new Set(dynamic.map(d => d.label.trim().toLowerCase()))
    let pIndex = dynamic.filter(d => d.project).length
    ;(field.project_rows || []).forEach(label => {
      if (seen.has(String(label).trim().toLowerCase())) return
      pIndex += 1
      dynamic.push({ key: `p${pIndex}`, vals: {}, label, project: true })
      seen.add(String(label).trim().toLowerCase())
    })
    // A grid with no named rows (the paper's "Discharge Location: 1. / 2.")
    // starts with its numbered blank lines, ready to fill (Phelipe 2026-09-07).
    if (fixed.length === 0 && dynamic.length === 0) {
      for (let i = 1; i <= (parseInt(field.extra_rows, 10) || 0); i++) dynamic.push({ key: `x${i}`, vals: {}, label: "", project: false })
    }
    dynamic.forEach((d, i) => { rows += this._tableRowHtml(field, name, d.key, d.vals, d.label, fixed.length + i + 1, true, d.project) })
    const cols = field.columns || []
    // Every grid can take another row (Phelipe 2026-09-07: "it's a good rule").
    const addBtn = `<button type="button" data-action="click->${this.identifier}#addTableRow" data-table-key="${this._escAttr(field.key)}"
           class="mt-2 inline-flex items-center gap-1 text-[13px] font-medium text-brand hover:underline cursor-pointer">+ ${this._escHtml(field.add_row_label || "Add row")}</button>`
    if (stacked) {
      // Sentence-style grids (the SWPPP discharge points): one card per row
      // with the full question wording and its instruction, on every screen.
      return `<div data-table-wrap="${this._escAttr(field.key)}"><div class="space-y-2" data-table-body="${this._escAttr(field.key)}" data-table-layout="stacked">${rows}</div>${addBtn}</div>`
    }
    const head = `<th class="px-2 py-1.5 text-left font-medium">${this._escHtml(field.row_label || "Item")}</th>` +
      cols.map(c => `<th class="px-2 py-1.5 text-left font-medium"${c.help ? ` title="${this._escAttr(c.help)}"` : ""}>${this._escHtml(c.label)}</th>`).join("") +
      `<th class="w-px whitespace-nowrap"></th>`
    return `<div class="sm:overflow-x-auto" data-table-wrap="${this._escAttr(field.key)}"><table class="block sm:table min-w-full sm:border border-gray-200 rounded-lg text-[13px]">
      <thead class="hidden sm:table-header-group bg-gray-50 text-gray-500"><tr>${head}</tr></thead><tbody class="block sm:table-row-group" data-table-body="${this._escAttr(field.key)}">${rows}</tbody></table>${addBtn}</div>`
  }

  // One grid row. Fixed rows show their label; added rows (editable) carry a
  // name input plus a × that drops the row — and, for a remembered project
  // row, tells the server to forget it.
  _tableRowHtml(field, name, rowKey, vals, label, number, editable, projectRow = false) {
    const cols = field.columns || []
    const stacked = field.layout === "stacked"
    const defaults = field.defaults && typeof field.defaults === "object" ? field.defaults : {}
    const rowDefaults = defaults[rowKey] || {}
    const cell = stacked ? "block" : "block sm:table-cell px-2 py-1.5 sm:border-b border-gray-200 align-top"
    const small = "w-full px-2 py-1 text-[13px] border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
    const colKey = c => c.key || String(c.label || "col").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "col"
    const num = `<span class="text-gray-400 mr-1" data-row-number>${number}.</span>`
    const cells = []
    // Photos on the item itself (Larry 2026-09-08): the report form draws a
    // paperclip + thumbnails under the row name; the template editor's
    // preview has no such hook and shows nothing.
    const savedPhotos = Array.isArray(vals.__photos) ? vals.__photos : []
    const photoStrip = typeof this._rowPhotoStrip === "function" ? this._rowPhotoStrip(field.key, rowKey, savedPhotos) : ""
    let tds = `<td class="${cell} text-[13px] font-semibold sm:font-medium text-gray-800 sm:text-gray-700 sm:min-w-[150px]">` +
      (editable
        ? `<div class="flex items-center gap-1">${num}<input type="text" name="${name}[${rowKey}][__label]" value="${this._escAttr(label || "")}" placeholder="${this._escAttr(field.row_label || "Item")}" class="${small}" data-row-label></div>`
        : num + this._escHtml(label)) + `</td>`
    cols.forEach(col => {
      const ck = colKey(col)
      const cname = `${name}[${rowKey}][${ck}]`
      const v = vals[ck] !== undefined && vals[ck] !== null ? vals[ck] : (rowDefaults[ck] != null ? rowDefaults[ck] : "")
      let input
      switch (col.type) {
        case "yes_no":
          input = `<div class="flex items-center gap-3 whitespace-nowrap">` + ["Yes", "No"].map(o =>
            `<label class="inline-flex items-center gap-1 cursor-pointer"><input type="radio" name="${cname}" value="${o}" ${v === o ? "checked" : ""} class="border-gray-300 text-brand focus:ring-brand"><span class="text-[13px] text-gray-600">${o}</span></label>`
          ).join("") + `</div>`
          break
        case "date":
          input = `<input type="text" name="${cname}" value="${this._escAttr(v)}" data-controller="datepicker" placeholder="Date" class="${small} min-w-[110px]">`
          break
        case "number":
          input = `<input type="number" name="${cname}" value="${this._escAttr(v)}" class="${small} min-w-[90px]">`
          break
        case "checkbox":
          input = `<input type="hidden" name="${cname}" value="0"><input type="checkbox" name="${cname}" value="1" ${v === "1" || v === true ? "checked" : ""} class="rounded border-gray-300 text-brand focus:ring-brand">`
          break
        case "textarea":
          input = `<textarea name="${cname}" rows="2" class="${small} min-w-[220px] resize-y">${this._escHtml(v)}</textarea>`
          break
        default:
          input = `<input type="text" name="${cname}" value="${this._escAttr(v)}" class="${small} min-w-[140px]">`
      }
      const cond = col.show_if && col.show_if.column
        ? ` data-show-if-col="${this._escAttr(col.show_if.column)}" data-show-if-equals="${this._escAttr(col.show_if.equals || "Yes")}"` : ""
      if (stacked) {
        cells.push(`<div data-cell-col="${this._escAttr(ck)}"${cond}>
          <div class="text-[13px] font-medium text-gray-700">${this._escHtml(col.label)}</div>
          ${col.help ? `<p class="text-[12px] text-gray-500 mt-0.5 mb-1.5">${this._escHtml(col.help)}</p>` : `<div class="mb-1.5"></div>`}
          ${input}
        </div>`)
      } else {
        tds += `<td class="${cell}" data-cell-col="${this._escAttr(ck)}"${cond}><div class="sm:hidden text-[12px] text-gray-500 mb-0.5">${this._escHtml(col.label)}</div>${col.help ? `<p class="sm:hidden text-[12px] text-gray-400 mb-1">${this._escHtml(col.help)}</p>` : ""}${input}</td>`
      }
    })
    if (stacked) {
      const removeBtn = editable
        ? `<button type="button" data-action="click->${this.identifier}#removeTableRow" title="Remove row" class="ml-auto text-gray-400 hover:text-red-500 text-[16px] leading-none cursor-pointer">×</button>` : ""
      const head = editable
        ? `<div class="flex items-center gap-1 flex-1">${num}<input type="text" name="${name}[${rowKey}][__label]" value="${this._escAttr(label || "")}" placeholder="${this._escAttr(field.row_label || "Item")}" class="${small} max-w-xs" data-row-label></div>`
        : `<div class="text-[13px] font-semibold text-gray-800">${num}${this._escHtml(label)}</div>`
      return `<div class="rounded-lg border border-gray-200 bg-white p-3 space-y-3" data-row-key="${this._escAttr(rowKey)}" ${projectRow ? 'data-project-row="1"' : ""}>
        <div class="flex items-center gap-2">${head}${removeBtn}</div>${cells.join("")}${photoStrip}</div>`
    }
    // Photos + flag sit in their own last column (Phelipe 2026-09-10) so the
    // item names stay one line; the × for added rows shares it.
    const removeCell = editable
      ? `<button type="button" data-action="click->${this.identifier}#removeTableRow" title="Remove row" class="text-gray-400 hover:text-red-500 text-[16px] leading-none cursor-pointer">×</button>` : ""
    tds += `<td class="${cell} sm:w-px sm:whitespace-nowrap"><div class="flex items-center gap-2">${photoStrip}${removeCell}</div></td>`
    return `<tr class="block sm:table-row border border-gray-200 sm:border-0 rounded-lg sm:rounded-none mb-2 sm:mb-0 p-1 sm:p-0" data-row-key="${this._escAttr(rowKey)}" ${projectRow ? 'data-project-row="1"' : ""}>${tds}</tr>`
  }

  addTableRow(e) {
    if (e) e.preventDefault()
    const key = e.currentTarget.dataset.tableKey
    const field = this._templateFieldByKey && this._templateFieldByKey(key)
    const body = this.element.querySelector(`[data-table-body="${CSS.escape(key)}"]`)
    if (!field || !body) return
    const used = [...body.querySelectorAll("[data-row-key]")].map(tr => tr.dataset.rowKey)
    let n = 1
    while (used.includes(`x${n}`)) n += 1
    body.insertAdjacentHTML("beforeend", this._tableRowHtml(field, `form_data[${key}]`, `x${n}`, {}, "", used.length + 1, true, false))
    this._renumberTable(body)
    this._applyShowIf && this._applyShowIf()
    body.lastElementChild?.querySelector("[data-row-label]")?.focus()
  }

  removeTableRow(e) {
    if (e) e.preventDefault()
    const tr = e.currentTarget.closest("[data-row-key]")
    const body = tr?.closest("[data-table-body]")
    if (!tr || !body) return
    const key = body.dataset.tableBody
    const label = tr.querySelector("[data-row-label]")?.value?.trim()
    if (tr.dataset.projectRow === "1" && label) {
      const wrap = body.closest("[data-table-wrap]")
      wrap?.insertAdjacentHTML("beforeend", `<input type="hidden" name="form_data[${this._escAttr(key)}][__remove_rows][]" value="${this._escAttr(label)}">`)
    }
    tr.remove()
    this._renumberTable(body)
  }

  _renumberTable(body) {
    body.querySelectorAll("[data-row-key]").forEach((tr, i) => {
      const n = tr.querySelector("[data-row-number]")
      if (n) n.textContent = `${i + 1}.`
    })
  }

  // ── Signature answers (Larry's SWPPP, 2026-09-07) ─────────────────────
  // A "Click to sign" block opens the type-or-draw modal below; the result
  // lives as JSON in a hidden input — {kind, name, data (drawn PNG), signed_at}
  // — and is drawn on the report page and the PDF.
  _parseSignature(raw) {
    if (!raw) return null
    if (typeof raw === "object") return raw
    try { const v = JSON.parse(raw); return v && typeof v === "object" ? v : null } catch (e) { return null }
  }

  _renderSignatureField(field, val, name) {
    let sig = this._parseSignature(val)
    // Remembered outside signer for this field on this project (Phelipe
    // 2026-09-07): a fresh report starts with the request already set.
    if (!sig && field.default_signer && field.default_signer.email) {
      sig = { kind: "request", name: field.default_signer.name || "", email: field.default_signer.email }
    }
    const key = this._escAttr(field.key)
    const hidden = `<input type="hidden" name="${name}" value="${this._escAttr(sig ? JSON.stringify(sig) : "")}" data-sig-input="${key}">`
    let body
    if (sig && sig.kind === "request" && sig.email) {
      const who = `<strong>${this._escHtml(sig.name || sig.email)}</strong>${sig.name ? ` <span class="opacity-70">· ${this._escHtml(sig.email)}</span>` : ""}`
      const declined = sig.status === "declined"
      const state = declined ? `declined to sign${sig.reason ? `: ${this._escHtml(sig.reason)}` : ""}`
        : sig.status === "sent" ? "has the signing link — waiting for their signature"
        : "gets an email to sign when the report is published"
      const tone = declined ? "border-red-300 bg-red-50 text-red-700" : "border-amber-300 bg-amber-50 text-amber-800"
      body = `<div class="rounded-lg border border-dashed ${tone} px-3 py-2.5 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div class="text-[13px] min-w-0">${who} ${state}</div>
        <div class="flex items-center gap-3 shrink-0 text-[13px]">
          <button type="button" data-action="click->${this.identifier}#openSignature" data-sig-key="${key}" data-sig-mode="request" class="underline cursor-pointer">Change</button>
          <button type="button" data-action="click->${this.identifier}#openSignature" data-sig-key="${key}" data-sig-mode="typed" class="underline cursor-pointer">Sign myself</button>
        </div>
      </div>`
    } else if (sig && (sig.name || sig.data)) {
      const lang = document.documentElement.lang || "en"
      const when = sig.signed_at ? new Date(sig.signed_at).toLocaleDateString(lang, { year: "numeric", month: "short", day: "numeric" }) : ""
      const mark = sig.kind === "drawn" && sig.data
        ? `<img src="${this._escAttr(sig.data)}" alt="Signature" class="max-h-14 max-w-full block">`
        : `<div class="text-[26px] leading-none text-gray-800" style="font-family: 'Allura', cursive;">${this._escHtml(sig.name || "")}</div>`
      body = `<div class="rounded-lg border border-gray-200 bg-white px-3 py-2.5 flex items-center justify-between gap-3">
        <div class="min-w-0">${mark}<div class="text-[12px] text-gray-500 mt-1">${this._escHtml(sig.name || "")}${when ? ` · ${when}` : ""}</div>${sig.comment ? `<div class="text-[13px] text-gray-700 mt-1"><span class="text-gray-500">Comment:</span> ${this._escHtml(sig.comment)}</div>` : ""}</div>
        <button type="button" data-action="click->${this.identifier}#openSignature" data-sig-key="${key}"
                class="text-[13px] text-gray-500 hover:text-gray-700 underline cursor-pointer shrink-0">Re-sign</button>
      </div>`
    } else {
      body = `<button type="button" data-action="click->${this.identifier}#openSignature" data-sig-key="${key}" data-sig-mode="typed"
        class="w-full h-14 rounded-lg border-2 border-dashed border-amber-400 bg-amber-50 hover:bg-amber-100 flex items-center justify-center gap-2 text-[13px] font-medium text-amber-700 cursor-pointer">
        ${this.signIconHtmlValue || ""} Click to sign
      </button>
      <button type="button" data-action="click->${this.identifier}#openSignature" data-sig-key="${key}" data-sig-mode="request"
        class="mt-1.5 text-[12px] text-gray-500 hover:text-gray-700 underline cursor-pointer">or send it to someone else to sign</button>`
    }
    return `<div data-sig-block="${key}">${hidden}${body}</div>`
  }

  _escHtml(str) {
    if (!str) return ""
    const div = document.createElement("div")
    div.textContent = String(str)
    return div.innerHTML
  }

  _escAttr(str) {
    if (!str) return ""
    return String(str).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  }
}

export class TemplateEditorMixin {
  // ── New report type popup (Larry K) ────────────────────────────────
  // Commanders add a type without leaving the half-filled report: the
  // popup POSTs to the settings endpoint, then the new type is appended
  // to the dropdown and selected (report number refreshes via typeChanged).

  openNewTypeModal() {
    if (!this.hasNewTypeModalTarget) return
    this.newTypeNameTarget.value = ""
    this._setNewTypeError(null)
    this.newTypeModalTarget.classList.remove("hidden")
    setTimeout(() => this.newTypeNameTarget.focus(), 50)
  }

  closeNewTypeModal() {
    if (this.hasNewTypeModalTarget) this.newTypeModalTarget.classList.add("hidden")
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  // Enter inside the popup adds the type — without this it would submit
  // the whole daily-report form underneath.
  newTypeEnter(event) {
    event.preventDefault()
    this.submitNewType()
  }

  async submitNewType() {
    const name = this.newTypeNameTarget.value.trim()
    if (!name) {
      this._setNewTypeError("Type a name first.")
      this.newTypeNameTarget.focus()
      return
    }

    const btn = this.hasNewTypeSubmitTarget ? this.newTypeSubmitTarget : null
    if (btn) { btn.disabled = true; btn.textContent = "Adding…" }
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch(this.createTypeUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ daily_report_type: { name, ...this._newTypeExtraParams() } })
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        this._setNewTypeError((data.errors || ["Could not create the type."]).join(", "))
        return
      }

      // The host page decides what to do with the new type (the report form
      // selects it; the settings list adds a row).
      this._onTemplateTypeSaved(data)
    } catch (_) {
      this._setNewTypeError("Network error — try again.")
    } finally {
      if (btn) { btn.disabled = false; btn.textContent = "Add" }
    }
  }

  _setNewTypeError(message) {
    if (!this.hasNewTypeErrorTarget) return
    this.newTypeErrorTarget.textContent = message || ""
    this.newTypeErrorTarget.classList.toggle("hidden", !message)
  }

  // ── 22.1: build a report type from an uploaded template ─────────
  // Drop/pick a PDF -> presign + PUT to S3 -> the extract endpoint has
  // Gemini read it -> the preview modal shows the drafted form for fixes
  // -> Save creates the FormTemplate + report type in one POST.

  get TEMPLATE_FIELD_TYPES() { return TEMPLATE_FIELD_TYPES }
  // Grid columns (Larry's SWPPP, 2026-09-07).
  get TP_TABLE_COLUMN_TYPES() { return TP_TABLE_COLUMN_TYPES }

  triggerTemplateFile() {
    if (this.hasNewTypeFileTarget) this.newTypeFileTarget.click()
  }

  templateDragOver(event) {
    event.preventDefault()
    this.newTypeDropTarget.classList.add("border-brand", "bg-brand/5")
  }

  templateDragLeave() {
    this.newTypeDropTarget.classList.remove("border-brand", "bg-brand/5")
  }

  templateDrop(event) {
    event.preventDefault()
    event.stopPropagation()
    this.templateDragLeave()
    const file = event.dataTransfer?.files?.[0]
    if (file) this._extractTemplate(file)
  }

  templateFileChosen(event) {
    const file = event.target.files?.[0]
    event.target.value = ""
    if (file) this._extractTemplate(file)
  }

  async _extractTemplate(file) {
    if (this._templateExtracting) return
    const ext = file.name.split(".").pop()?.toLowerCase()
    if (!["pdf", "png", "jpg", "jpeg", "webp", "heic"].includes(ext)) {
      this._setNewTypeError("Use a PDF (or a photo of the form).")
      return
    }
    if (file.size > 18 * 1024 * 1024) {
      this._setNewTypeError("That file is over 18MB — export a lighter PDF and try again.")
      return
    }

    this._setNewTypeError("")
    this._setTemplateBusy(true)
    this._startTemplateAiStatus("Uploading the template...")
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const presignRes = await fetch(this.presignUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        body: JSON.stringify({ files: [{ filename: file.name, content_type: file.type || "application/octet-stream", byte_size: file.size }] })
      })
      const presign = await presignRes.json().catch(() => ({}))
      if (!presignRes.ok) throw new Error(presign.error || "Upload refused")
      const upload = presign.uploads[0]

      const putRes = await fetch(upload.upload_url, { method: "PUT", headers: { "Content-Type": upload.content_type }, body: file })
      if (!putRes.ok) throw new Error("Upload failed — try again")

      this._setTemplateAiMessage("Reading the template and building the form...")
      const extractRes = await fetch(this.extractTemplateUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": token },
        body: JSON.stringify({ cache_name: upload.cache_name, filename: file.name })
      })
      const data = await extractRes.json().catch(() => ({}))
      if (!extractRes.ok) throw new Error(data.error || "Couldn't read the template")

      this._templateDraft = data.schema
      this._templateSourceName = file.name
      this._stopTemplateAiStatus()
      if (this.hasTemplatePreviewNameTarget) {
        this.templatePreviewNameTarget.value = (this.hasNewTypeNameTarget && this.newTypeNameTarget.value.trim()) || data.name || ""
      }
      this._setTemplatePreviewError("")
      this._tpSelection = null
      this._renderTemplateFormPane()
      this._renderTemplateInspector()
      this.templatePreviewModalTarget.classList.remove("hidden")
    } catch (err) {
      this._stopTemplateAiStatus()
      this._setNewTypeError(err.message || "Couldn't read the template — try again.")
    } finally {
      this._setTemplateBusy(false)
    }
  }

  // Lock the popup while AI works: the Add button and the drop zone both
  // wait it out, so nothing double-fires under the extraction.
  _setTemplateBusy(busy) {
    this._templateExtracting = busy
    if (this.hasNewTypeSubmitTarget) {
      this.newTypeSubmitTarget.disabled = busy
      this.newTypeSubmitTarget.classList.toggle("opacity-50", busy)
      this.newTypeSubmitTarget.classList.toggle("cursor-not-allowed", busy)
    }
    if (this.hasNewTypeDropTarget) {
      this.newTypeDropTarget.classList.toggle("pointer-events-none", busy)
      this.newTypeDropTarget.classList.toggle("opacity-50", busy)
    }
  }

  // Shimmering "ConsLog AI — ..." line with an elapsed timer, same look as
  // the Specs & Drawings extraction banner.
  _startTemplateAiStatus(message) {
    if (!this.hasNewTypeAiStatusTarget) return
    this._aiStatusStart = performance.now()
    clearInterval(this._aiStatusTimer)
    this._setTemplateAiMessage(message)
    this.newTypeAiStatusTarget.classList.remove("hidden")
    this._aiStatusTimer = setInterval(() => {
      const timerEl = this.newTypeAiStatusTarget.querySelector("[data-ai-timer]")
      if (timerEl) timerEl.textContent = `${Math.round((performance.now() - this._aiStatusStart) / 1000)}s`
    }, 1000)
  }

  _setTemplateAiMessage(message) {
    if (!this.hasNewTypeAiStatusTarget) return
    const secs = Math.round((performance.now() - (this._aiStatusStart || performance.now())) / 1000)
    this.newTypeAiStatusTarget.innerHTML =
      `<span class="font-bold ai-rainbow-text">ConsLog AI — ${this._escHtml(message)}</span> ` +
      `<span data-ai-timer class="text-gray-500 font-normal tabular-nums inline-block w-8 text-left">${secs}s</span>`
  }

  _stopTemplateAiStatus() {
    clearInterval(this._aiStatusTimer)
    this._aiStatusTimer = null
    if (!this.hasNewTypeAiStatusTarget) return
    this.newTypeAiStatusTarget.innerHTML = ""
    this.newTypeAiStatusTarget.classList.add("hidden")
  }

  _setTemplatePreviewError(message) {
    if (!this.hasTemplatePreviewErrorTarget) return
    this.templatePreviewErrorTarget.textContent = message || ""
    this.templatePreviewErrorTarget.classList.toggle("hidden", !message)
  }

  closeTemplatePreview(event) {
    if (event && event.target !== event.currentTarget && event.currentTarget === this.templatePreviewModalTarget) return
    if (this.hasTemplatePreviewModalTarget) this.templatePreviewModalTarget.classList.add("hidden")
  }

  // ── Template editor (Google-Forms style, Phelipe 2026-07-22) ──
  // The FORM is the surface: the left pane renders it exactly as the crew
  // will see it; clicking a question (or a section title) opens its details
  // in the right-hand panel. this._templateDraft is the single source of
  // truth — the inspector mutates it and the form pane re-renders.

  get TP_OPTION_TYPES() { return TP_OPTION_TYPES }

  _renderTemplateFormPane() {
    if (!this.hasTemplatePreviewBodyTarget) return
    const pane = this.templatePreviewBodyTarget
    const scroll = pane.scrollTop
    const schema = this._templateDraft || { sections: [] }
    const sel = this._tpSelection
    const esc = (s) => this._escHtml(String(s ?? ""))

    let html = ""
    ;(schema.sections || []).forEach((section, si) => {
      const sectionSelected = sel && sel.si === si && sel.fi == null
      const count = (section.fields || []).length
      html += `<div class="mb-6">`
      html += `<div data-action="click->${this.identifier}#tpPick" data-tp-si="${si}"
                    class="flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 -mx-2 cursor-pointer transition-colors ${sectionSelected ? "bg-brand/10 ring-1 ring-brand" : "hover:bg-gray-100"}">
        <h3 class="text-sm font-semibold text-gray-700">${esc(section.label || `Section ${si + 1}`)}</h3>
        <span class="text-[11px] text-gray-400">${count} field${count === 1 ? "" : "s"}</span>
      </div>`
      html += `<div class="grid grid-cols-1 sm:grid-cols-6 gap-3 mt-2">`
      ;(section.fields || []).forEach((field, fi) => {
        const colClass = { third: "sm:col-span-2", half: "sm:col-span-3", full: "sm:col-span-6" }[field.width] || "sm:col-span-6"
        const isSel = sel && sel.si === si && sel.fi === fi
        html += `<div class="${colClass}">
          <div data-action="click->${this.identifier}#tpPick dragstart->${this.identifier}#tpDragStart dragover->${this.identifier}#tpDragOver drop->${this.identifier}#tpDrop dragend->${this.identifier}#tpDragEnd"
               draggable="true" data-tp-si="${si}" data-tp-fi="${fi}"
               class="h-full rounded-lg p-2 -m-1 cursor-pointer transition-colors ${isSel ? "bg-brand/10 ring-1 ring-brand" : "hover:bg-gray-100"}">
            <div class="pointer-events-none">${this._renderFormField(field, undefined)}</div>
          </div>
        </div>`
      })
      html += `</div>`
      html += `<button type="button" data-action="click->${this.identifier}#tpAddField" data-tp-si="${si}"
                       class="mt-2 text-[13px] font-medium text-brand hover:text-brand-dark cursor-pointer">+ Add field</button>`
      html += `</div>`
    })
    html += `<button type="button" data-action="click->${this.identifier}#tpAddSection"
                     class="w-full px-3 py-2 text-[13px] font-medium text-brand border-2 border-dashed border-gray-200 rounded-xl hover:border-brand hover:bg-brand/5 cursor-pointer">+ Add section</button>`

    pane.innerHTML = html
    // The rendered inputs are display-only here (clicking selects the
    // question). The modal lives INSIDE the report <form>: strip names so
    // nothing submits with the real report, and nested controllers so
    // widgets (datepicker) don't stack on re-renders.
    pane.querySelectorAll("[name]").forEach(el => el.removeAttribute("name"))
    pane.querySelectorAll("[data-controller]").forEach(el => el.removeAttribute("data-controller"))
    pane.scrollTop = scroll
  }

  // ── Drag & drop in the template editor (Phelipe 2026-07-22) ──────────
  // Live-layout preview: while dragging, the original card hides, a dashed
  // placeholder occupies the exact landing spot, and the other fields
  // reflow around it — what you see is what the drop does. Top/bottom half
  // of a target = move before/after it; its LEFT/RIGHT EDGE = share the
  // row (a full-width target previews and splits into halves).
  _tpColFor(width) {
    return { third: "sm:col-span-2", half: "sm:col-span-3", full: "sm:col-span-6" }[width] || "sm:col-span-6"
  }

  tpDragStart(e) {
    const card = e.currentTarget
    this._tpDrag = { si: +card.dataset.tpSi, fi: +card.dataset.tpFi }
    this._tpSourceWrap = card.parentElement
    this._tpSourceHeight = Math.max(card.getBoundingClientRect().height, 52)
    e.dataTransfer.effectAllowed = "move"
    try { e.dataTransfer.setData("text/plain", "tp-field") } catch { /* some engines insist */ }
    // Hide AFTER the browser captures the drag image, so the ghost gap
    // opens up and the rest of the form reflows into place.
    setTimeout(() => { if (this._tpDrag) this._tpSourceWrap.style.display = "none" })
  }

  tpDragOver(e) {
    if (!this._tpDrag) return
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"
    const card = e.currentTarget
    const r = card.getBoundingClientRect()
    const x = (e.clientX - r.left) / r.width
    const zone = x < 0.25 ? "left" : x > 0.75 ? "right"
               : ((e.clientY - r.top) / r.height < 0.5 ? "before" : "after")
    if (card === this._tpTargetCard && zone === this._tpDragZone) return
    this._tpTargetCard = card
    this._tpDragZone = zone
    this._tpPreviewAt(card, zone)
  }

  // Position the dashed placeholder (and temporarily resize a full-width
  // target for the side-by-side zones) so the layout previews the drop.
  _tpPreviewAt(card, zone) {
    const draft = this._templateDraft
    const wrap = card.parentElement
    const grid = wrap.parentElement
    this._tpRestorePreviewWidth()

    const dragged = draft.sections[this._tpDrag.si]?.fields?.[this._tpDrag.fi]
    const target = draft.sections[+card.dataset.tpSi]?.fields?.[+card.dataset.tpFi]
    if (!dragged || !target) return

    if (!this._tpGhost) {
      this._tpGhost = document.createElement("div")
      this._tpGhost.setAttribute("data-tp-ghost", "1")
      this._tpGhost.innerHTML = `<div class="h-full rounded-lg border-2 border-dashed border-brand bg-brand/5"></div>`
      this._tpGhost.addEventListener("dragover", (ev) => { ev.preventDefault(); ev.dataTransfer.dropEffect = "move" })
      this._tpGhost.addEventListener("drop", (ev) => { ev.preventDefault(); ev.stopPropagation(); this._tpCommitPending() })
    }
    this._tpGhost.style.minHeight = `${this._tpSourceHeight}px`

    if (zone === "left" || zone === "right") {
      const shared = (target.width || "full") === "full" ? "half" : target.width
      this._tpPreviewWrap = { el: wrap, originalClass: wrap.className }
      wrap.className = this._tpColFor(shared)
      this._tpGhost.className = this._tpColFor(shared)
      grid.insertBefore(this._tpGhost, zone === "left" ? wrap : wrap.nextSibling)
    } else {
      this._tpGhost.className = this._tpColFor(dragged.width)
      grid.insertBefore(this._tpGhost, zone === "before" ? wrap : wrap.nextSibling)
    }
  }

  _tpRestorePreviewWidth() {
    if (this._tpPreviewWrap) {
      this._tpPreviewWrap.el.className = this._tpPreviewWrap.originalClass
      this._tpPreviewWrap = null
    }
  }

  tpDrop(e) {
    if (!this._tpDrag) return
    e.preventDefault()
    e.stopPropagation()
    this._tpTargetCard = e.currentTarget
    this._tpDragZone = this._tpDragZone || "after"
    this._tpCommitPending()
  }

  _tpCommitPending() {
    if (this._tpDrag && this._tpTargetCard) {
      const to = { si: +this._tpTargetCard.dataset.tpSi, fi: +this._tpTargetCard.dataset.tpFi }
      this._tpMoveField(this._tpDrag, to, this._tpDragZone || "after")
    }
    this._tpEndDrag()
  }

  tpDragEnd() { this._tpEndDrag() }

  _tpEndDrag() {
    this._tpRestorePreviewWidth()
    if (this._tpGhost?.parentElement) this._tpGhost.parentElement.removeChild(this._tpGhost)
    if (this._tpSourceWrap) this._tpSourceWrap.style.display = ""
    this._tpDrag = null
    this._tpTargetCard = null
    this._tpDragZone = null
    this._tpSourceWrap = null
    this._renderTemplateFormPane()
    this._renderTemplateInspector()
  }

  _tpMoveField(from, to, zone) {
    const draft = this._templateDraft
    if (!draft || (from.si === to.si && from.fi === to.fi)) return
    const src = draft.sections[from.si]
    const dst = draft.sections[to.si]
    if (!src || !dst) return

    const [field] = src.fields.splice(from.fi, 1)
    if (!field) return
    let ti = to.fi
    if (from.si === to.si && from.fi < to.fi) ti-- // removal shifted the target left

    const target = dst.fields[ti]
    if ((zone === "left" || zone === "right") && target) {
      if ((target.width || "full") === "full") target.width = "half"
      field.width = target.width || "half"
      dst.fields.splice(zone === "left" ? ti : ti + 1, 0, field)
    } else {
      dst.fields.splice(zone === "before" ? ti : ti + 1, 0, field)
    }

    this._tpSelection = { si: to.si, fi: dst.fields.indexOf(field) }
    this._templateChanged()
  }

  _tpTypeLabel(t) {
    return {
      text: "Short text", textarea: "Long text / notes", number: "Number",
      date: "Date", time: "Time", checkbox: "Yes/No checkbox",
      radio: "Pick one (radio)", select: "Dropdown", multiselect: "Pick several",
      checklist: "Checklist", table: "Table (rows × columns)", signature: "Signature (click to sign)",
      photo: "Photo upload", file: "File upload", gps: "GPS location", weather: "Weather"
    }[t] || t
  }

  _renderTemplateInspector() {
    if (!this.hasTemplateInspectorTarget) return
    const sel = this._tpSelection
    const draft = this._templateDraft || { sections: [] }
    const esc = (s) => this._escHtml(String(s ?? ""))

    if (!sel || !draft.sections[sel.si]) {
      this.templateInspectorTarget.innerHTML = `<div class="px-4 py-10 text-center text-[13px] text-gray-400">Click any question on the form to edit it here.</div>`
      return
    }
    const section = draft.sections[sel.si]

    if (sel.fi == null) {
      this.templateInspectorTarget.innerHTML = `
        <div class="px-4 py-4 space-y-4">
          <p class="text-[13px] font-semibold text-gray-900">Section</p>
          <div>
            <label class="block text-[13px] font-medium text-gray-700 mb-1">Section title</label>
            <input type="text" value="${esc(section.label)}" data-tp-prop="label"
                   data-action="input->${this.identifier}#tpInspectorInput"
                   class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand">
          </div>
          <button type="button" data-action="click->${this.identifier}#tpRemoveSelected"
                  class="w-full px-3 py-1.5 text-[13px] font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg cursor-pointer">Remove section</button>
        </div>`
      return
    }

    const field = section.fields?.[sel.fi]
    if (!field) {
      this._tpSelection = null
      return this._renderTemplateInspector()
    }
    const type = KNOWN_FIELD_TYPES.includes(field.type) ? field.type : "text"
    const typeChoices = this.TEMPLATE_FIELD_TYPES.includes(type) ? this.TEMPLATE_FIELD_TYPES : [...this.TEMPLATE_FIELD_TYPES, type]
    const showOptions = this.TP_OPTION_TYPES.includes(type)
    this.templateInspectorTarget.innerHTML = `
      <div class="px-4 py-4 space-y-4">
        <p class="text-[13px] font-semibold text-gray-900">Question</p>
        <div>
          <label class="block text-[13px] font-medium text-gray-700 mb-1">Label</label>
          <input type="text" value="${esc(field.label)}" data-tp-prop="label"
                 data-action="input->${this.identifier}#tpInspectorInput"
                 class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand">
        </div>
        <div>
          <label class="block text-[13px] font-medium text-gray-700 mb-1">Answer type</label>
          <select data-tp-prop="type" data-action="change->${this.identifier}#tpInspectorInput"
                  class="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg cursor-pointer focus:outline-none focus:ring-1 focus:ring-brand">
            ${typeChoices.map(t => `<option value="${t}" ${t === type ? "selected" : ""}>${this._tpTypeLabel(t)}</option>`).join("")}
          </select>
        </div>
        <div class="${showOptions ? "" : "hidden"}">
          <label class="block text-[13px] font-medium text-gray-700 mb-1">Options <span class="font-normal text-gray-400">(one per line)</span></label>
          <textarea rows="6" data-tp-prop="options" data-action="input->${this.identifier}#tpInspectorInput"
                    class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand">${esc((field.options || []).join("\n"))}</textarea>
        </div>
        <div class="${["text", "textarea", "number", "date", "time"].includes(type) ? "" : "hidden"}">
          <label class="block text-[13px] font-medium text-gray-700 mb-1">Pre-filled value <span class="font-normal text-gray-400">(optional${type === "date" ? " — \"today\" fills the current date" : ""})</span></label>
          <input type="text" value="${esc(field.default || "")}" data-tp-prop="default" data-action="input->${this.identifier}#tpInspectorInput"
                 class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand">
        </div>
        <div class="${type === "table" ? "" : "hidden"} space-y-3">
          <div>
            <label class="block text-[13px] font-medium text-gray-700 mb-1">Rows <span class="font-normal text-gray-400">(one per line)</span></label>
            <textarea rows="5" data-tp-prop="rows" data-action="input->${this.identifier}#tpInspectorInput"
                      class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand">${esc((field.rows || []).join("\n"))}</textarea>
          </div>
          <div>
            <label class="block text-[13px] font-medium text-gray-700 mb-1">Columns <span class="font-normal text-gray-400">(one per line: Label | yes_no, text, textarea, number, date or checkbox | hint)</span></label>
            <textarea rows="5" data-tp-prop="columns" data-action="input->${this.identifier}#tpInspectorInput"
                      class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand">${esc((field.columns || []).map(c => `${c.label} | ${c.type || "text"}${c.help ? ` | ${c.help}` : ""}`).join("\n"))}</textarea>
          </div>
          <div class="flex gap-3">
            <div class="flex-1">
              <label class="block text-[13px] font-medium text-gray-700 mb-1">First column title</label>
              <input type="text" value="${esc(field.row_label || "Item")}" data-tp-prop="row_label" data-action="input->${this.identifier}#tpInspectorInput"
                     class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand">
            </div>
            <div class="w-28">
              <label class="block text-[13px] font-medium text-gray-700 mb-1">Blank rows</label>
              <input type="number" min="0" max="10" value="${parseInt(field.extra_rows, 10) || 0}" data-tp-prop="extra_rows" data-action="input->${this.identifier}#tpInspectorInput"
                     class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand">
            </div>
          </div>
        </div>
        <div class="${type === "table" ? "hidden" : ""}">
          <label class="block text-[13px] font-medium text-gray-700 mb-1">Size</label>
          <select data-tp-prop="width" data-action="change->${this.identifier}#tpInspectorInput"
                  class="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg cursor-pointer focus:outline-none focus:ring-1 focus:ring-brand">
            <option value="third" ${field.width === "third" ? "selected" : ""}>Small (1/3 row)</option>
            <option value="half" ${field.width === "half" || !field.width ? "selected" : ""}>Half row</option>
            <option value="full" ${field.width === "full" ? "selected" : ""}>Full row</option>
          </select>
        </div>
        <label class="inline-flex items-center gap-2 text-[13px] text-gray-700 cursor-pointer">
          <input type="checkbox" data-tp-prop="required" ${field.required ? "checked" : ""}
                 data-action="change->${this.identifier}#tpInspectorInput"
                 class="w-4 h-4 rounded border-gray-300 text-brand"> Required
        </label>
        ${this._tpShowIfHtml(sel, field)}
        <label class="${["text", "number", "date"].includes(type) ? "" : "hidden"} inline-flex items-start gap-2 text-[13px] text-gray-700 cursor-pointer">
          <input type="checkbox" data-tp-prop="remember" ${field.remember === "user" ? "checked" : ""}
                 data-action="change->${this.identifier}#tpInspectorInput"
                 class="mt-0.5 w-4 h-4 rounded border-gray-300 text-brand">
          <span>Remember this person's last answer <span class="text-gray-400">(pre-filled on their next report, any project — e.g. a certification number)</span></span>
        </label>
        <button type="button" data-action="click->${this.identifier}#tpRemoveSelected"
                class="w-full px-3 py-1.5 text-[13px] font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg cursor-pointer">Remove question</button>
      </div>`
  }

  tpPick(event) {
    const el = event.currentTarget
    const si = parseInt(el.dataset.tpSi, 10)
    const fi = el.dataset.tpFi != null ? parseInt(el.dataset.tpFi, 10) : null
    this._tpSelection = { si, fi }
    this._renderTemplateFormPane()
    this._renderTemplateInspector()
  }

  tpAddField(event) {
    const si = parseInt(event.currentTarget.dataset.tpSi, 10)
    const section = this._templateDraft?.sections?.[si]
    if (!section) return
    if (!section.fields) section.fields = []
    section.fields.push({ label: "New question", type: "text", required: false, width: "half" })
    this._tpSelection = { si, fi: section.fields.length - 1 }
    this._templateChanged()
    this._renderTemplateFormPane()
    this._renderTemplateInspector()
    this.templateInspectorTarget.querySelector("[data-tp-prop='label']")?.select()
  }

  tpAddSection() {
    if (!this._templateDraft) this._templateDraft = { sections: [] }
    this._templateDraft.sections.push({ label: "New section", fields: [{ label: "New question", type: "text", required: false, width: "half" }] })
    this._tpSelection = { si: this._templateDraft.sections.length - 1, fi: null }
    this._templateChanged()
    this._renderTemplateFormPane()
    this._renderTemplateInspector()
  }

  tpRemoveSelected() {
    const sel = this._tpSelection
    if (!sel) return
    const sections = this._templateDraft?.sections || []
    if (sel.fi == null) sections.splice(sel.si, 1)
    else sections[sel.si]?.fields?.splice(sel.fi, 1)
    this._tpSelection = null
    this._templateChanged()
    this._renderTemplateFormPane()
    this._renderTemplateInspector()
  }

  // One handler for every inspector input: data-tp-prop names the property.
  // Typing re-renders ONLY the form pane (debounced) so focus never leaves
  // the panel; discrete changes (type) also rebuild the panel itself.
  tpInspectorInput(event) {
    const sel = this._tpSelection
    if (!sel || !this._templateDraft) return
    const target = sel.fi == null
      ? this._templateDraft.sections[sel.si]
      : this._templateDraft.sections[sel.si]?.fields?.[sel.fi]
    if (!target) return

    const prop = event.currentTarget.dataset.tpProp
    if (prop === "required") target.required = event.currentTarget.checked
    else if (prop === "remember") { if (event.currentTarget.checked) target.remember = "user"; else delete target.remember }
    else if (prop === "options") target.options = event.currentTarget.value.split("\n").map(s => s.trim()).filter(Boolean)
    else if (prop === "rows") target.rows = event.currentTarget.value.split("\n").map(s => s.trim()).filter(Boolean)
    else if (prop === "columns") target.columns = event.currentTarget.value.split("\n").map(s => s.trim()).filter(Boolean).map((line, i) => {
      // "Label | type | hint" — a column's other settings (its key, an
      // "if yes" rule from the import) ride along from the same line before.
      const [label, ctype, help] = line.split("|").map(s => (s || "").trim())
      const prior = (target.columns || [])[i] || {}
      const col = { ...prior, label, type: this.TP_TABLE_COLUMN_TYPES.includes(ctype) ? ctype : "text" }
      if (help) col.help = help
      else delete col.help
      return col
    })
    else if (prop === "show_if") {
      if (event.currentTarget.value) target.show_if = { field: event.currentTarget.value, equals: "Yes" }
      else delete target.show_if
    }
    else if (prop === "extra_rows") target.extra_rows = Math.max(0, Math.min(10, parseInt(event.currentTarget.value, 10) || 0))
    else target[prop] = event.currentTarget.value

    this._templateChanged()
    if (prop === "type") {
      if (this.TP_OPTION_TYPES.includes(target.type) && !(target.options || []).length) target.options = ["Yes", "No"]
      if (target.type === "table") {
        target.width = "full"
        if (!(target.rows || []).length) target.rows = ["Item 1"]
        if (!(target.columns || []).length) target.columns = [{ label: "Yes / No", type: "yes_no" }, { label: "Notes", type: "text" }]
        if (!target.row_label) target.row_label = "Item"
      }
      this._renderTemplateInspector()
    }

    clearTimeout(this._tpPaneTimer)
    this._tpPaneTimer = setTimeout(() => this._renderTemplateFormPane(), 200)
  }

  // Final schema for saving: trimmed labels, unique snake_case keys (AI keys
  // kept when present), whitelisted widths, options where they belong.
  _finalizeTemplateDraft() {
    const used = new Set()
    const slug = (label) => {
      let base = String(label || "field").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40) || "field"
      let key = base, n = 1
      while (used.has(key)) { n += 1; key = `${base}_${n}` }
      used.add(key)
      return key
    }
    const sections = (this._templateDraft?.sections || []).map((section, si) => ({
      key: `section_${si + 1}`,
      label: String(section.label || "").trim() || `Section ${si + 1}`,
      fields: (section.fields || []).filter(f => String(f.label || "").trim()).map(f => {
        const type = KNOWN_FIELD_TYPES.includes(f.type) ? f.type : "text"
        const out = {
          key: f.key && !used.has(f.key) ? (used.add(f.key), f.key) : slug(f.label),
          type,
          label: String(f.label).trim(),
          required: !!f.required,
          width: type === "signature" ? "full" : (["third", "half", "full"].includes(f.width) ? f.width : (["textarea", "checklist"].includes(type) ? "full" : "half"))
        }
        if (this.TP_OPTION_TYPES.includes(type)) {
          out.options = (f.options || []).map(o => String(o).trim()).filter(Boolean)
          if (!out.options.length) out.options = ["Yes", "No"]
        }
        if (!this.TP_OPTION_TYPES.includes(type) && type !== "table" && f.default != null && String(f.default).trim()) {
          out.default = String(f.default).trim()
        }
        if (f.show_if && f.show_if.field) out.show_if = { field: String(f.show_if.field), equals: String(f.show_if.equals || "Yes") }
        if (f.help_text && String(f.help_text).trim()) out.help_text = String(f.help_text).trim()
        if (f.remember === "user" && ["text", "number", "date"].includes(type)) out.remember = "user"
        if (f.placeholder && String(f.placeholder).trim()) out.placeholder = String(f.placeholder).trim()
        if (type === "table") {
          out.width = "full"
          if (f.layout === "stacked") out.layout = "stacked"
          if (f.defaults && typeof f.defaults === "object") out.defaults = f.defaults
          out.row_label = String(f.row_label || "Item").trim() || "Item"
          out.rows = (f.rows || []).map(r => String(r).trim()).filter(Boolean)
          out.extra_rows = Math.max(0, Math.min(10, parseInt(f.extra_rows, 10) || 0))
          const ckeys = new Set()
          out.columns = (f.columns || []).filter(c => c && String(c.label || "").trim()).map(c => {
            const ctype = this.TP_TABLE_COLUMN_TYPES.includes(c.type) ? c.type : "text"
            const base = String(c.label).toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40) || "col"
            let key = c.key && !ckeys.has(c.key) ? c.key : base, n = 1
            while (ckeys.has(key)) { n += 1; key = `${base}_${n}` }
            ckeys.add(key)
            const col = { key, label: String(c.label).trim(), type: ctype }
            if (c.help && String(c.help).trim()) col.help = String(c.help).trim()
            if (c.show_if && c.show_if.column) col.show_if = { column: String(c.show_if.column), equals: String(c.show_if.equals || "Yes") }
            return col
          })
          if (!out.columns.length) out.columns = [{ key: "notes", label: "Notes", type: "text" }]
        }
        return out
      })
    })).filter(s => s.fields.length > 0)
    return { sections }
  }

  saveTemplateType() { return this._saveTemplateType(false) }

  // "Update its form": the existing type keeps its number and reports; only
  // the form changes (Phelipe 2026-09-07).
  replaceTemplateType() { return this._saveTemplateType(true) }

  renameTemplateType() {
    this._hideTemplateConflict()
    if (this.hasTemplatePreviewNameTarget) { this.templatePreviewNameTarget.focus(); this.templatePreviewNameTarget.select() }
  }

  _hideTemplateConflict() {
    if (this.hasTemplatePreviewConflictTarget) this.templatePreviewConflictTarget.classList.add("hidden")
  }

  async _saveTemplateType(replaceExisting) {
    this._hideTemplateConflict()
    const name = this.hasTemplatePreviewNameTarget ? this.templatePreviewNameTarget.value.trim() : ""
    if (!name) {
      this._setTemplatePreviewError("Give the report type a name first.")
      this.templatePreviewNameTarget.focus()
      return
    }
    const finalized = this._finalizeTemplateDraft()
    if (!finalized.sections.length) {
      this._setTemplatePreviewError("The form needs at least one field.")
      return
    }

    const btn = this.hasTemplatePreviewSaveTarget ? this.templatePreviewSaveTarget : null
    if (btn) { btn.disabled = true; btn.textContent = "Saving…" }
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch(this.createTypeUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ daily_report_type: {
          name,
          form_template_schema: JSON.stringify(finalized),
          source_filename: this._templateSourceName || "",
          replace_existing: replaceExisting ? "1" : "0",
          ...this._newTypeExtraParams()
        } })
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        if (data.name_taken && this.hasTemplatePreviewConflictTarget) {
          if (this.hasTemplatePreviewConflictNameTarget) this.templatePreviewConflictNameTarget.textContent = name
          this.templatePreviewConflictTarget.classList.remove("hidden")
          this._setTemplatePreviewError("")
          return
        }
        this._setTemplatePreviewError((data.errors || ["Could not save the report type."]).join(", "))
        return
      }

      this._onTemplateTypeSaved(data)
    } catch (_) {
      this._setTemplatePreviewError("Network error — try again.")
    } finally {
      if (btn) { btn.disabled = false; btn.textContent = "Save report type" }
    }
  }

  // Success toast (flash-toast convention: auto-dismisses via the flash
  // Stimulus controller).
  _flashToast(message) {
    const el = document.createElement("div")
    el.className = "flash-toast-notice"
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.textContent = message
    document.body.prepend(el)
  }

  // ── Show-only-if (parity with the old settings builder, 2026-09-09) ──
  // A question can hide until a Yes/No answer elsewhere on the form is Yes.
  _tpShowIfHtml(sel, field) {
    const candidates = this._tpShowIfCandidates(sel)
    if (!candidates.length) return ""
    const current = field.show_if && field.show_if.field
    return `<div>
          <label class="block text-[13px] font-medium text-gray-700 mb-1">Show only if…</label>
          <select data-tp-prop="show_if" data-action="change->${this.identifier}#tpInspectorInput"
                  class="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg cursor-pointer focus:outline-none focus:ring-1 focus:ring-brand">
            <option value="">Always shown</option>
            ${candidates.map(c => `<option value="${this._escAttr(c.key)}" ${c.key === current ? "selected" : ""}>${this._escHtml(c.label)} = Yes</option>`).join("")}
          </select>
        </div>`
  }

  _tpShowIfCandidates(sel) {
    const draft = this._templateDraft || { sections: [] }
    const out = []
    ;(draft.sections || []).forEach((section, si) => (section.fields || []).forEach((f, fi) => {
      if (si === sel.si && fi === sel.fi) return
      const yesNo = f.type === "checkbox" ||
        (["radio", "select"].includes(f.type) && (f.options || []).some(o => String(o).trim().toLowerCase() === "yes"))
      if (!yesNo) return
      out.push({ key: this._tpEnsureKey(f), label: f.label || "Untitled" })
    }))
    return out
  }

  // A rule needs the controlling question's key; questions typed in the
  // editor only get one at save time, so hand it out early here.
  _tpEnsureKey(f) {
    if (f.key) return f.key
    f.key = String(f.label || "field").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40) || "field"
    return f.key
  }

  // Hooks the host controller may override.
  _templateChanged() {}
  _newTypeExtraParams() { return {} }
  _onTemplateTypeSaved(data) {
    this.closeTemplatePreview()
    this.closeNewTypeModal()
    this._flashToast(`Report type “${data.name}” added.`)
  }
}
